import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CandlestickChart from './CandlestickChart';
import OrderBookComponent from './OrderBookComponent';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { useProMode } from '../context/ProModeContext';
import { useNotification } from '../context/NotificationContext';
import socket from '../services/socket';
import { API_BASE_URL } from '../config';

// Human-readable event type names
const EVENT_TYPE_LABELS = {
  'MARKET_EVENT': 'Market Event',
  'CUSTOM_STOCK_EVENT': 'Stock Event',
  'MERGER': 'Merger',
  'SCANDAL': 'Scandal',
  'EARNINGS_BEAT': 'Earnings Beat',
  'EARNINGS_MISS': 'Earnings Miss',
  'CFO_RESIGNATION': 'CFO Resignation',
  'INSIDER_TRADING': 'Insider Trading',
  'ANALYST_UPGRADE': 'Analyst Upgrade',
  'ANALYST_DOWNGRADE': 'Analyst Downgrade',
  'EXECUTIVE_SCANDAL': 'Executive Scandal',
  'VIRAL_MOMENT': 'Viral Moment',
  'PRODUCT_LAUNCH': 'Product Launch',
  'REGULATORY_FINE': 'Regulatory Fine',
  'bull_run': 'Bull Run',
  'market_crash': 'Market Crash',
  'volatility': 'Volatility',
  'positive': 'Positive',
  'negative': 'Negative',
  'neutral': 'Neutral'
};

const formatEventType = (type) => {
  return EVENT_TYPE_LABELS[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Event';
};

const formatMultiplierAsPercent = (multiplier) => {
  if (!multiplier || multiplier === 1) return null;
  const percent = (multiplier - 1) * 100;
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(0)}%`;
};

const VALID_TABS = ['chart', 'about', 'depth', 'events', 'positions'];
const TAB_ALIASES = { orderbook: 'depth', info: 'about', stats: 'about' }; // Support old tab names

const StockDetailPage = ({ userId, positions: propPositions }) => {
  const { stockId, tab: urlTab } = useParams();
  const navigate = useNavigate();
  const { isProMode } = useProMode();
  const { addNotification } = useNotification();

  // Normalize tab from URL (handle aliases and invalid values)
  const normalizeTab = (tab) => {
    if (!tab) return 'chart';
    const normalized = TAB_ALIASES[tab] || tab;
    return VALID_TABS.includes(normalized) ? normalized : 'chart';
  };

  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => normalizeTab(urlTab));
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [priceLog, setPriceLog] = useState([]); // Debug: track price changes
  const [marketEvents, setMarketEvents] = useState([]); // Market events log
  const [marketStatus, setMarketStatus] = useState({ isOpen: false, tradingEnabled: false, phase: 'not_started' });
  const [showCeoPicture, setShowCeoPicture] = useState(null);
  const [userPositions, setUserPositions] = useState(null); // Local positions state (null = not yet fetched)

  // Prefer local state once fetched, fall back to props
  const positions = userPositions !== null ? userPositions : (propPositions || []);

  // Mobile detection
  const isMobile = window.innerWidth <= 768;

  // Handle tab change with URL update
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Update URL without full page reload
    const basePath = `/stock/${stockId}`;
    navigate(newTab === 'chart' ? basePath : `${basePath}/${newTab}`, { replace: true });
  };

  // Scroll to top when navigating to a stock page
  // .main-content has overflow-y: auto, so it's the actual scroll container
  useEffect(() => {
    const main = document.querySelector('.main-content');
    if (main) main.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, [stockId]);

  // Sync activeTab when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const normalized = normalizeTab(urlTab);
    if (normalized !== activeTab) {
      setActiveTab(normalized);
    }
  }, [urlTab]);

  // Fetch comprehensive stock data
  // isRefresh=true means don't show loading spinner (prevents chart unmount)
  const fetchStockData = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      setError(null);

      // Determine if stockId is a ticker or an ID
      const isTickerFormat = !stockId.startsWith('stock-');
      const apiUrl = isTickerFormat
        ? `${API_BASE_URL}/api/stocks/ticker/${stockId}`
        : `${API_BASE_URL}/api/stocks/${stockId}`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        if (response.status === 404) {
          setError(`Stock not found: ${stockId}`);
          return;
        }
        throw new Error(`Failed to fetch stock data: ${response.statusText}`);
      }

      const data = await response.json();
      setStockData(data);

      // Fetch events now that we have stock data
      fetchMarketEvents(data);

    } catch (err) {
      console.error('Error fetching stock data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch recent market events for this stock (server-side filtered)
  const fetchMarketEvents = async (currentStockData = null) => {
    const stockInfo = currentStockData || stockData;

    if (!stockInfo?.id && !stockInfo?.ticker) {
      return;
    }

    try {
      // Server filters by stockId — returns only events affecting this stock + market-wide
      const response = await fetch(`${API_BASE_URL}/api/events?limit=200&stockId=${encodeURIComponent(stockInfo.id)}`);
      if (response.ok) {
        const events = await response.json();
        const formattedEvents = (Array.isArray(events) ? events : []).map(event => ({
          ...event,
          time: new Date(event.timestamp || event.created_at).toLocaleTimeString(),
          isMarketWide: event.affectedStocks === 'ALL' || event.affectedStocks === 'all'
        }));
        setMarketEvents(formattedEvents);
      }
    } catch (error) {
      console.error('Error fetching market events:', error);
    }
  };

  // Fetch market status
  const fetchMarketStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/party-state`);
      if (response.ok) {
        const data = await response.json();
        if (data.partyState) {
          setMarketStatus({
            isOpen: data.partyState.phase === 'trading',
            tradingEnabled: data.partyState.phase === 'trading',
            phase: data.partyState.phase
          });
        }
      }
    } catch (error) {
      console.error('Error fetching market status:', error);
    }
  };

  // Fetch user positions directly
  const fetchUserPositions = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/positions/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUserPositions(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching user positions:', error);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    if (stockId) {
      fetchStockData(); // This also fetches events after stock data loads
      fetchMarketStatus();
      fetchUserPositions(); // Fetch user positions

      // Set up periodic refresh (pass true to prevent loading spinner)
      const interval = setInterval(() => fetchStockData(true), 30000); // Refresh every 30 seconds
      setRefreshInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [stockId, userId]);

  // Listen for market state updates
  useEffect(() => {
    if (!socket) return;

    const handlePartyStateUpdate = (data) => {
      if (data.partyState) {
        setMarketStatus({
          isOpen: data.partyState.phase === 'trading',
          tradingEnabled: data.partyState.phase === 'trading',
          phase: data.partyState.phase
        });
      }
    };

    const handleMarketBell = (data) => {
      if (data.type === 'OPEN') {
        setMarketStatus(prev => ({ ...prev, isOpen: true, tradingEnabled: true, phase: 'trading' }));
      } else if (data.type === 'CLOSE' || data.type === 'TRADING_PAUSED') {
        setMarketStatus(prev => ({ ...prev, isOpen: false, tradingEnabled: false }));
      }
    };

    socket.on('party-state-update', handlePartyStateUpdate);
    socket.on('market-bell', handleMarketBell);

    return () => {
      socket.off('party-state-update', handlePartyStateUpdate);
      socket.off('market-bell', handleMarketBell);
    };
  }, []);

  // Handle real-time updates via WebSocket
  // Uses window.socket (raw Socket.IO instance) to bypass the wrapper proxy
  // which can lose listeners when the socket is re-created.
  // Only depends on [stockId] to avoid cleanup/re-attach cycles from stockData changes.
  useEffect(() => {
    if (!stockId) return;
    let cancelled = false;
    let attachedSocket = null;

    const handleStockUpdate = (data) => {
      if (data.stockId === stockId) {
        setPriceLog(prev => [{
          time: new Date().toLocaleTimeString(),
          stockId: data.stockId,
          price: data.currentPrice,
          source: 'websocket'
        }, ...prev.slice(0, 19)]);
        setStockData(prevData => {
          if (!prevData) return prevData;
          return { ...prevData, currentPrice: data.currentPrice };
        });
      }
    };

    const handleBulkUpdate = (data) => {
      const stocksArray = Array.isArray(data) ? data : data?.stocks;
      if (!stocksArray || stocksArray.length === 0) return;
      const matchingStock = stocksArray.find(s =>
        s.ticker === stockId ||
        s.ticker?.toUpperCase() === stockId?.toUpperCase()
      );
      if (matchingStock) {
        setPriceLog(prev => [{
          time: new Date().toLocaleTimeString(),
          stockId: matchingStock.id,
          price: matchingStock.currentPrice,
          source: 'stock-updates'
        }, ...prev.slice(0, 19)]);
        setStockData(prevData => {
          if (!prevData) return prevData;
          const newPrice = matchingStock.currentPrice;
          const basePrice = prevData.basePrice || prevData.currentPrice;
          const change24h = newPrice - basePrice;
          const changePercent24h = basePrice > 0 ? ((newPrice - basePrice) / basePrice) * 100 : 0;
          return {
            ...prevData,
            currentPrice: newPrice,
            change24h: parseFloat(change24h.toFixed(4)),
            changePercent24h: parseFloat(changePercent24h.toFixed(2)),
          };
        });
      }
    };

    const handleMarketEvent = (event) => {
      const isMarketWide = event.affectedStocks === 'ALL' ||
                           event.affectedStocks === 'all' ||
                           (Array.isArray(event.affectedStocks) &&
                            event.affectedStocks.some(s => s.ticker === 'ALL' || s === 'ALL'));
      let affectsThisStock = false;
      if (Array.isArray(event.affectedStocks)) {
        affectsThisStock = event.affectedStocks.some(s => {
          if (typeof s === 'object') {
            return s.ticker === stockId || s.ticker?.toUpperCase() === stockId?.toUpperCase();
          }
          return s === stockId;
        });
      }
      if (!affectsThisStock) {
        affectsThisStock = (event.ticker && (event.ticker === stockId || event.ticker?.toUpperCase() === stockId?.toUpperCase()));
      }
      if (isMarketWide || affectsThisStock) {
        setMarketEvents(prev => [{
          ...event,
          time: new Date().toLocaleTimeString(),
          isMarketWide
        }, ...prev.slice(0, 49)]);
      }
    };

    function attach(sock) {
      attachedSocket = sock;
      sock.on('stock-price-update', handleStockUpdate);
      sock.on('stock-updates', handleBulkUpdate);
      sock.on('stocks-update', handleBulkUpdate);
      sock.on('market-update', handleBulkUpdate);
      sock.on('market-event', handleMarketEvent);
    }

    function detach() {
      if (!attachedSocket) return;
      attachedSocket.off('stock-price-update', handleStockUpdate);
      attachedSocket.off('stock-updates', handleBulkUpdate);
      attachedSocket.off('stocks-update', handleBulkUpdate);
      attachedSocket.off('market-update', handleBulkUpdate);
      attachedSocket.off('market-event', handleMarketEvent);
      attachedSocket = null;
    }

    // Attach immediately if socket exists, otherwise poll briefly
    if (window.socket) {
      attach(window.socket);
    } else {
      let attempts = 0;
      const retryTimer = setInterval(() => {
        attempts++;
        if (cancelled) { clearInterval(retryTimer); return; }
        if (window.socket) {
          clearInterval(retryTimer);
          attach(window.socket);
        } else if (attempts >= 20) {
          clearInterval(retryTimer);
        }
      }, 250);
    }

    return () => {
      cancelled = true;
      detach();
    };
  }, [stockId]);

  // Handle position updates from chart trades
  const handlePositionUpdate = (result, actionDetails) => {
    if (result.error) {
      addNotification(`Trade failed: ${result.error}`, 'error');
    } else {
      const { actionType, stockTicker, tradeAmount } = actionDetails;
      let message = '';

      if (actionType === 'CLOSE') {
        message = `Closed position in ${stockTicker}`;
      } else if (actionType.startsWith('OPEN_')) {
        const type = actionType.replace('OPEN_', '');
        message = `Opened ${type} position: ${tradeAmount} shares of ${stockTicker}`;
      }

      addNotification(message, 'success');

      // Re-fetch positions and stock data immediately after trade
      fetchUserPositions();
      fetchStockData(true);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="stock-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="stock-detail-page">
        <div className="error-container">
          <h2>Error Loading Stock</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={() => fetchStockData()} className="retry-button">
              Try Again
            </button>
            <button onClick={() => navigate('/market')} className="back-button">
              Back to Market
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!stockData) {
    return (
      <div className="stock-detail-page">
        <div className="no-data-container">
          <h2>Stock Not Found</h2>
          <p>The requested stock could not be found.</p>
          <button onClick={() => navigate('/market')} className="back-button">
            Back to Market
          </button>
        </div>
      </div>
    );
  }

  const { name, ticker, currentPrice, changePercent24h, stats, openPositions, description, ceo_name, ceo_description, ceo_picture_url } = stockData;

  // Helper to get market status text
  const getMarketStatusText = () => {
    if (marketStatus.phase === 'trading' && marketStatus.tradingEnabled) {
      return { text: 'MARKET OPEN', className: 'status-open' };
    } else if (marketStatus.phase === 'macro_game') {
      return { text: 'TRADING PAUSED - MACRO GAME', className: 'status-paused' };
    } else if (marketStatus.phase === 'ended') {
      return { text: 'MARKET CLOSED', className: 'status-closed' };
    } else if (marketStatus.phase === 'premarket') {
      return { text: 'PRE-MARKET', className: 'status-premarket' };
    } else {
      return { text: 'MARKET CLOSED', className: 'status-closed' };
    }
  };

  const statusInfo = getMarketStatusText();
  const canTrade = marketStatus.phase === 'trading' && marketStatus.tradingEnabled;

  return (
    <div className="stock-detail-page">
      {/* Market Status Banner */}
      {!canTrade && (
        <div className={`market-status-banner ${statusInfo.className}`}>
          <span className="status-indicator"></span>
          <span className="status-text">{statusInfo.text}</span>
          {marketStatus.phase !== 'trading' && (
            <span className="status-message">Trading is currently disabled</span>
          )}
        </div>
      )}

      {/* Header Section - Compact */}
      <div className="stock-header">
        <div className="stock-title">
          <button onClick={() => navigate('/market')} className="back-button">
            ← Back
          </button>
          <div className="title-info">
            <span className="ticker">{ticker}</span>
            <span className="stock-name-inline">{name}</span>
          </div>
        </div>
        {description && (
          <p className="stock-description">{description}</p>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab ${activeTab === 'chart' ? 'active' : ''}`}
          onClick={() => handleTabChange('chart')}
        >
          Chart
        </button>
        <button
          className={`tab ${activeTab === 'about' || activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => handleTabChange('about')}
        >
          Info
        </button>
        <button
          className={`tab ${activeTab === 'depth' ? 'active' : ''}`}
          onClick={() => handleTabChange('depth')}
        >
          Depth
        </button>
        <button
          className={`tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => handleTabChange('events')}
        >
          Events
        </button>
        <button
          className={`tab ${activeTab === 'positions' ? 'active' : ''}`}
          onClick={() => handleTabChange('positions')}
        >
          Positions
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'chart' && (
          <div className="chart-tab">
            <CandlestickChart
              stockId={stockData.id}
              ticker={ticker}
              positions={positions?.filter(p => p.stockId === stockData.id) || []}
              isProMode={isProMode}
              onPositionUpdate={handlePositionUpdate}
              canTrade={canTrade}
              userId={userId}
            />
          </div>
        )}

        {activeTab === 'depth' && (
          <div className="orderbook-tab">
            <OrderBookComponent
              stockId={stockData.id}
              ticker={ticker}
              currentPrice={currentPrice}
              userId={userId}
              onPositionUpdate={handlePositionUpdate}
            />
          </div>
        )}

        {/* Stats tab redirects to about */}

        {activeTab === 'events' && (
          <div className="events-tab" style={{ padding: isMobile ? '8px' : '15px' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#00ff65', fontSize: isMobile ? '0.9em' : '1em' }}>Events for {stockData?.ticker || 'This Stock'}</h3>
            <div style={{
              maxHeight: isMobile ? '250px' : '400px',
              overflowY: 'auto',
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '6px',
              padding: isMobile ? '6px' : '10px'
            }}>
              {marketEvents.length === 0 ? (
                <div style={{ color: '#666', textAlign: 'center', padding: isMobile ? '20px' : '40px' }}>
                  <p style={{ fontSize: isMobile ? '0.85em' : '1em' }}>No events affecting this stock yet...</p>
                  <p style={{ fontSize: isMobile ? '0.75em' : '0.9em' }}>Stock-specific and market-wide events will appear here</p>
                </div>
              ) : (
                marketEvents.map((event, idx) => {
                  const impactPercent = formatMultiplierAsPercent(event.multiplier);
                  const isPositive = event.multiplier > 1;
                  const isNegative = event.multiplier < 1;
                  return (
                  <div key={idx} style={{
                    padding: isMobile ? '8px' : '12px',
                    marginBottom: isMobile ? '4px' : '8px',
                    background: event.isMarketWide ? 'rgba(0, 188, 212, 0.1)' : 'rgba(0, 255, 101, 0.1)',
                    borderRadius: '4px',
                    borderLeft: `3px solid ${isPositive ? '#00ff65' : isNegative ? '#ff4444' : '#00bcd4'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          color: isPositive ? '#00ff65' : isNegative ? '#ff4444' : '#00bcd4',
                          fontWeight: 'bold',
                          fontSize: isMobile ? '0.75em' : '1em'
                        }}>
                          {formatEventType(event.type)}
                        </span>
                        {impactPercent && (
                          <span style={{
                            fontSize: isMobile ? '0.7em' : '0.85em',
                            padding: '2px 6px',
                            background: isPositive ? 'rgba(0, 255, 101, 0.2)' : 'rgba(255, 68, 68, 0.2)',
                            borderRadius: '3px',
                            color: isPositive ? '#00ff65' : '#ff4444',
                            fontWeight: 'bold'
                          }}>
                            {impactPercent}
                          </span>
                        )}
                        {event.isMarketWide && (
                          <span style={{
                            fontSize: isMobile ? '0.55em' : '0.7em',
                            padding: '2px 4px',
                            background: 'rgba(0, 188, 212, 0.3)',
                            borderRadius: '3px',
                            color: '#00bcd4'
                          }}>
                            MARKET
                          </span>
                        )}
                      </div>
                      <span style={{ color: '#666', fontSize: isMobile ? '0.7em' : '0.85em' }}>{event.time}</span>
                    </div>
                    <p style={{ margin: 0, color: '#ccc', fontSize: isMobile ? '0.8em' : '1em' }}>{event.description || 'Market event occurred'}</p>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'positions' && (
          <div className="positions-tab">
            <div className="user-positions-section">
              <h3>Your Positions in {ticker}</h3>
              {positions && positions.filter(p => p.stockId === stockData.id).length > 0 ? (
                <div className="positions-list">
                  {positions.filter(p => p.stockId === stockData.id).map((position, index) => {
                    const profit = position.type === 'LONG'
                      ? (currentPrice - position.entryPrice) * position.shares
                      : (position.entryPrice - currentPrice) * position.shares;

                    const profitPercent = ((profit / (position.entryPrice * position.shares)) * 100);

                    return (
                      <div key={position.id || index} className={`position-card detailed ${position.type.toLowerCase()}`}>
                        <div className="position-header">
                          <div className="position-type">
                            <span className="type-badge">{position.type}</span>
                            <span className="shares">{position.shares} shares</span>
                          </div>
                          <div className="position-value">
                            <span className="current-value">
                              {formatCurrency(position.shares * currentPrice)}
                            </span>
                          </div>
                        </div>

                        <div className="position-details">
                          <div className="detail-row">
                            <span className="label">Entry Price:</span>
                            <span className="value">{formatCurrency(position.entryPrice)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Current Price:</span>
                            <span className="value">{formatCurrency(currentPrice)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Initial Value:</span>
                            <span className="value">{formatCurrency(position.initialValue || position.entryPrice * position.shares)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">P&L:</span>
                            <span className={`value ${profit >= 0 ? 'positive' : 'negative'}`}>
                              {profit >= 0 ? '+' : ''}{formatCurrency(profit)}
                              {' '}({profit >= 0 ? '+' : ''}{profitPercent.toFixed(2)}%)
                            </span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Opened:</span>
                            <span className="value">
                              {position.timestamp ? new Date(position.timestamp).toLocaleString() : 'Unknown'}
                            </span>
                          </div>
                        </div>

                        <div className="position-actions">
                          <button
                            className="close-position-btn"
                            onClick={() => handlePositionUpdate}
                          >
                            Close Position
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-positions">
                  <p>You don't have any positions in {ticker} yet.</p>
                  <p>Use the chart tab to open your first position!</p>
                </div>
              )}
            </div>

            {/* All Market Positions */}
            <div className="all-positions-section">
              <h3>All Market Positions ({stockData.allPositions?.length || 0})</h3>
              {stockData.allPositions && stockData.allPositions.length > 0 ? (
                <div className="all-positions-table">
                  <div className="positions-table-header">
                    <span>User</span>
                    <span>Type</span>
                    <span>Shares</span>
                    <span>Entry</span>
                    <span>Value</span>
                    <span>P&L</span>
                  </div>
                  <div className="positions-table-body">
                    {stockData.allPositions.map((pos, index) => {
                      const pnl = pos.type === 'LONG'
                        ? (currentPrice - pos.entryPrice) * pos.shares
                        : (pos.entryPrice - currentPrice) * pos.shares;
                      const pnlPercent = ((pnl / (pos.entryPrice * pos.shares)) * 100);
                      const isCurrentUser = pos.userId === userId;

                      return (
                        <div
                          key={pos.id || index}
                          className={`positions-table-row ${pos.type.toLowerCase()} ${isCurrentUser ? 'current-user' : ''}`}
                        >
                          <span
                            className="username clickable"
                            onClick={() => navigate(`/portfolio/${pos.userId}`)}
                          >
                            {pos.username} {isCurrentUser && '(You)'}
                          </span>
                          <span className="type">{pos.type}</span>
                          <span className="shares">{pos.shares}</span>
                          <span className="entry">{formatCurrency(pos.entryPrice)}</span>
                          <span className="value">{formatCurrency(pos.shares * currentPrice)}</span>
                          <span className={`pnl ${pnl >= 0 ? 'positive' : 'negative'}`}>
                            {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)}
                            <small> ({pnl >= 0 ? '+' : ''}{pnlPercent.toFixed(1)}%)</small>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="no-positions">
                  <p>No open positions in {ticker}.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {(activeTab === 'about' || activeTab === 'stats') && (
          <div className="about-tab">
            {/* Company Info Section */}
            <div className="company-info-section">
              <div className="ticker-badge">${ticker}</div>
              <h2 className="company-name">{name}</h2>
              {description && (
                <p className="company-description">{description}</p>
              )}
            </div>

            {/* CEO Section - Photo with name */}
            <div className="ceo-section">
              <div className="ceo-photo-container" onClick={() => ceo_picture_url && setShowCeoPicture(ceo_picture_url)} style={{ cursor: ceo_picture_url ? 'pointer' : 'default' }}>
                {ceo_picture_url ? (
                  <img
                    src={ceo_picture_url}
                    alt={`${ceo_name || 'CEO'}`}
                    className="ceo-photo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="ceo-photo-placeholder" style={{ display: ceo_picture_url ? 'none' : 'flex' }}>
                  👤
                </div>
              </div>
              <div className="ceo-info">
                <span className="ceo-label">CEO</span>
                <span className={`ceo-name ${!ceo_name || ceo_name === 'TBD' ? 'tbd' : ''}`}>
                  {ceo_name || 'TBD'}
                </span>
                {ceo_description && (
                  <p className="ceo-description">{ceo_description}</p>
                )}
              </div>
            </div>

            {/* Commercial Video */}
            {ticker && (
              <div className="stock-commercial-section">
                <h3>Commercial</h3>
                <video
                  controls
                  playsInline
                  preload="metadata"
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    borderRadius: '6px',
                    background: '#000',
                  }}
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                >
                  <source src={`https://komokshow.netlify.app/clips/commercial/${ticker.toLowerCase()}_commercial.mp4`} type="video/mp4" />
                </video>
              </div>
            )}

            {/* Price & Trading Stats */}
            <div className="stock-statistics-detailed">
              <div className="stats-section">
                <h3>Price Information</h3>
                <div className="price-info-grid">
                  <div className="info-item">
                    <label>Current Price</label>
                    <span>{formatCurrency(currentPrice)}</span>
                  </div>
                  <div className="info-item">
                    <label>Base Price</label>
                    <span>{formatCurrency(stockData.basePrice)}</span>
                  </div>
                  <div className="info-item">
                    <label>24h Change</label>
                    <span className={changePercent24h >= 0 ? 'positive' : 'negative'}>
                      {formatCurrency(stockData.change24h || 0)} ({changePercent24h?.toFixed(2)}%)
                    </span>
                  </div>
                  <div className="info-item">
                    <label>24h High</label>
                    <span>{formatCurrency(stats?.dayHigh || 0)}</span>
                  </div>
                  <div className="info-item">
                    <label>24h Low</label>
                    <span>{formatCurrency(stats?.dayLow || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="stats-section">
                <h3>Trading Statistics</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <label>Total Volume</label>
                    <span className="value">{stats?.totalVolume?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="stat-card">
                    <label>Buy Volume</label>
                    <span className="value">{stats?.buyVolume?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="stat-card">
                    <label>Sell Volume</label>
                    <span className="value">{stats?.sellVolume?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="stat-card">
                    <label>Trade Count</label>
                    <span className="value">{stats?.tradeCount || 0}</span>
                  </div>
                  <div className="stat-card">
                    <label>Average Price</label>
                    <span className="value">{formatCurrency(stats?.avgPrice || 0)}</span>
                  </div>
                  <div className="stat-card">
                    <label>Volatility</label>
                    <span className="value">{(stockData.volatility * 100)?.toFixed(2)}%</span>
                  </div>
                </div>
              </div>

              {openPositions && openPositions.length > 0 && (
                <div className="stats-section">
                  <h3>Market Positions</h3>
                  <div className="market-positions">
                    {openPositions.map((position, index) => (
                      <div key={index} className="market-position-card">
                        <div className="position-type">{position.type}</div>
                        <div className="position-stats">
                          <span>Positions: {position.positionCount}</span>
                          <span>Total Shares: {position.totalShares}</span>
                          <span>Avg Entry: {formatCurrency(position.avgEntryPrice)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Refresh Controls */}
      {/* <div className="refresh-controls">
        <button onClick={() => fetchStockData(true)} className="refresh-button">
          🔄 Refresh Data
        </button>
        <span className="last-updated">
          Last updated: {new Date().toLocaleTimeString()}
        </span>
      </div> */}

      {/* Debug: Price Update Log */}
      {/* <div className="price-log" style={{
        marginTop: '20px',
        padding: '15px',
        background: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '8px',
        border: '1px solid #333'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#00ff65' }}>
          Price Update Log (WebSocket)
          <span style={{ fontSize: '0.8em', color: '#888', marginLeft: '10px' }}>
            Stock ID: {stockData?.id || stockId}
          </span>
        </h4>
        <div style={{
          maxHeight: '200px',
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: '12px'
        }}>
          {priceLog.length === 0 ? (
            <div style={{ color: '#666' }}>
              No price updates received yet... Waiting for WebSocket events.
              <br />
              <span style={{ color: '#888' }}>
                (If market is open, updates should appear every few seconds)
              </span>
            </div>
          ) : (
            priceLog.map((entry, idx) => (
              <div key={idx} style={{
                padding: '4px 8px',
                borderBottom: '1px solid #222',
                color: entry.isMatch ? '#00ff65' : '#888'
              }}>
                <span style={{ color: '#666' }}>[{entry.time}]</span>
                {' '}
                <span style={{ color: '#00bcd4' }}>{entry.source}</span>
                {' → '}
                <span>{entry.stockId}</span>
                {': '}
                <span style={{ color: '#fff' }}>${entry.price?.toFixed(2)}</span>
              </div>
            ))
          )}
        </div>
      </div> */}
      {showCeoPicture && (
        <div className="picture-lightbox" onClick={() => setShowCeoPicture(null)}>
          <img src={showCeoPicture} alt="CEO" />
        </div>
      )}
    </div>
  );
};

export default StockDetailPage;